import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import { getExtractions, loadExtraction, setFilters } from '../../store/extractions/action'; // ← add setFilters
import { capitalizeFirst } from '../../commonUtils/string.helper';
import { hasPermission } from '../../commonUtils/permission.helper';
import constants from '../../commonUtils/constants';
import IconButton from '../../components/IconButton';
import { EyeIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import UpdateProjectDetails from '../../pages/extraction/history/UpdateProjectDetails';
import { sanitizeId } from '../../commonUtils/extraction.helper.js';

const ExtHistoryContext = createContext();

export const useExtHistory = () => useContext(ExtHistoryContext);

export const ExtHistoryProvider = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.Auth);
    const { filters, ExtractionsList, extLoading, errors } = useSelector((state) => state.Extractions);
    const currentUserId = String(user?.id || user?._id || '');

    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [updateData, setUpdateData] = useState({ _id: "", title: "" });
    
    const [showUpdateProjectModal, setShowUpdateProjectModal] = useState(false);
    const [selectedExtractionForUpdate, setSelectedExtractionForUpdate] = useState(null);

    // ─── Search ──────────────────────────────────────────────────────────────
    // Local controlled value for the input so typing feels instant,
    // while the actual Redux filter (and fetch) is debounced.
    const [searchKeyword, setSearchKeyword] = useState(filters?.keyword || '');

    const debouncedSetFilter = useMemo(() =>
        debounce((keyword) => {
            dispatch(setFilters({ ...filters, keyword }));
        }, 400),
    [dispatch, filters]);

    const handleSearch = useCallback((e) => {
        const keyword = typeof e === 'string' ? e : e.target.value;
        setSearchKeyword(keyword);
        debouncedSetFilter(keyword);
    }, [debouncedSetFilter]);

    const handleClearSearch = useCallback(() => {
        setSearchKeyword('');
        dispatch(setFilters({ ...filters, keyword: '' }));
    }, [dispatch, filters]);
    // ─────────────────────────────────────────────────────────────────────────

    const debouncedGetExtractions = useMemo(() => debounce((params) => {
        dispatch(getExtractions(params));
    }, 300), [dispatch]);

    useEffect(() => {
        debouncedGetExtractions({
            currentPage: ExtractionsList?.pagination?.currentPage || 1,
            perPage: ExtractionsList?.pagination?.perPage || constants.tableUI.defaultPerpage,
            ...filters,
        });
    }, [filters, ExtractionsList?.pagination?.currentPage, ExtractionsList?.pagination?.perPage, debouncedGetExtractions]);

    useEffect(() => {
        handleCloseModal('update');
        handleCloseModal('delete');
        handleCloseModal('updateProject');
    }, [ExtractionsList]);

    const extractions = useMemo(() => ExtractionsList?.data?.map(
        ({ _id, key, name, status, extracted_by, temp_project_name, temp_master_project_name, project_list, approx_starting_price, pincode }, index) => ({
            creatorId: String(extracted_by?._id || extracted_by?.id || ''),
            index: index + 1,
            _id,
            key,
            name,
            isCreator: String(extracted_by?._id || extracted_by?.id || '') === currentUserId,
            creatorName: extracted_by?.name,
            tempProjectName: temp_project_name,
            tempMasterProjectName: temp_master_project_name,
            hasTempProjectData: Boolean(temp_project_name || temp_master_project_name),
            status: capitalizeFirst(status),
            approxStartingPrice: approx_starting_price != null ? String(approx_starting_price) : '',
            pincode: pincode || '',
            projectList: project_list || []
        })
    ), [ExtractionsList, currentUserId]);

    const pagination = useMemo(() => ExtractionsList?.pagination, [ExtractionsList]);
        
    const handlePageChange = useCallback((currentPage, perPage) => {
        debouncedGetExtractions({ currentPage, perPage, ...filters });
    }, [debouncedGetExtractions, filters]);

    const columnHelper = createColumnHelper();
    const canView = hasPermission('extractions.view');
    const canCrud = hasPermission('extractions.crud');

    const columns = useMemo(() => {
        const cols = [
            columnHelper.accessor('index', {
                header: '#',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('key', {
                header: 'Key',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('name', {
                header: 'Name',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('creatorName', {
                header: 'Owner',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('status', {
                header: 'Status',
                cell: info => info.getValue(),
            }),
        ];

        if (canView || canCrud) {
            cols.push(
                columnHelper.display({
                    id: 'actions',
                    header: 'Actions',
                    cell: info => (
                        <>
                            {canCrud && info.row.original.isCreator && (
                                <IconButton
                                    variant="action_default"
                                    size="icon"
                                    Icon={EyeIcon}
                                    onClick={() =>
                                        dispatch(
                                            loadExtraction({
                                                key: info.row.original.key,
                                                project_list: info.row.original.projectList,
                                                navigate,
                                                path: `/extraction/${info.row.original.key}`,
                                            })
                                        )
                                    }
                                />
                            )}

                            {canView && !info.row.original.isCreator && (
                                <IconButton
                                    variant="action_default"
                                    size="icon"
                                    Icon={EyeIcon}
                                    onClick={() =>
                                        dispatch(
                                            loadExtraction({
                                                viewMode: true,
                                                key: info.row.original.key,
                                                navigate,
                                                path: `/extraction/${info.row.original.key}`,
                                            })
                                        )
                                    }
                                />
                            )}

                            {canCrud && info.row.original.isCreator && info.row.original.hasTempProjectData && (
                                <IconButton
                                    variant="action_default"
                                    size="icon"
                                    Icon={PencilSquareIcon}
                                    onClick={() => {
                                        setSelectedExtractionForUpdate(sanitizeId(info.row.original));
                                        setShowUpdateProjectModal(true);
                                    }}
                                />
                            )}
                        </>
                    ),
                })
            );
        }

        return cols;
    }, [columnHelper, canView, canCrud, dispatch, navigate]);

    const table = useReactTable({
        data: extractions,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageIndex: ExtractionsList?.pagination?.currentPage - 1,
                pageSize: ExtractionsList?.pagination?.perPage,
            },
        },
        manualPagination: true,
        pageCount: ExtractionsList?.pagination?.totalPages,
    });

    const editExtraction = (data) => setUpdateData(data);
    
    const handleCloseModal = (action) => {
        if (action === "update") {
            setUpdateData({ id: "", name: "" });
            setOpenUpdateModal(false);
        } else if (action === "delete") {
            setShowDeleteConfirmationDialog(false);
        } else if (action === "updateProject") {
            setSelectedExtractionForUpdate(null);
            setShowUpdateProjectModal(false);
        }
    };
    
    const askDeleteConfirmation = (id) => {
        setShowDeleteConfirmationDialog(true);
    };
    
    const handleDelete = () => {
        handleCloseModal('delete');
    };

    return (
        <ExtHistoryContext.Provider value={{
            extractions,
            table,
            columns,
            loading: extLoading,
            errors,
            pagination,
            handlePageChange,
            editExtraction,
            openUpdateModal,
            setOpenUpdateModal,
            updateData,
            handleCloseModal,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
            // ── Search ──────────────────
            searchKeyword,
            handleSearch,
            handleClearSearch,
        }}>
            {children}
            
            {showUpdateProjectModal && selectedExtractionForUpdate && (
                <UpdateProjectDetails
                    isOpen={showUpdateProjectModal}
                    onClose={() => handleCloseModal('updateProject')}
                    extractionData={selectedExtractionForUpdate}
                />
            )}
        </ExtHistoryContext.Provider>
    );
};